import { useState, FC, useEffect, useRef } from 'react';
import styles from './Search.module.scss';
import clsx from 'clsx';
import HTMLReactParser from 'html-react-parser';
import { Button } from '../../Button/Button';
import { LoupeIcon } from '../../Icons/Loupe';
import { CrossIcon } from '../../Icons/Cross';
import { useGetSearchData } from '../../../queries/search/useGetSearchData';
import { useDebounce } from '../../../hooks/useDebounce';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { CircularProgress } from '@mui/material';
import { IGetSearchDataResponse } from '../../../types/search';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { useTypedSelector } from '../../../lib/hooks/useTypedSelector';
import { commodityTypes } from '../../../data/commodityTypes';
import { Link } from 'react-router-dom';

export interface ISearch {
  //eslint-disable-next-line
  onSearch: (value: string) => void;
  onReset?: () => void;
  isFullSearch?: boolean;
  className?: string;
  fullScreen?: boolean;
}

const Search: FC<ISearch> = ({
  onSearch,
  isFullSearch,
  onReset,
  className,
  fullScreen,
}) => {
  const suggestsRef = useRef(null);
  const inputRef = useRef(null);
  const [isShowSuggests, setIsShowSuggests] = useState<boolean>(false);
  const [items, setItems] = useState<IGetSearchDataResponse>([]);
  const [loadedCount, setLoadedCount] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const search = useTypedSelector((state) =>
    decodeURIComponent(state.search.search),
  );

  const [infiniteRef] = useInfiniteScroll({
    loading: false,
    hasNextPage: hasMore,
    onLoadMore: () => loadMoreItems(),
  });
  const [searchValue, setSearchValue] = useState<string>(
    fullScreen ? '' : search,
  );
  const {
    searchDataMutate,
    searchData,
    isGetSearchDataPending,
    isGetSearchDataSuccess,
  } = useGetSearchData();
  const debouncedSearchValue = useDebounce(searchValue, 500);

  const loadMoreItems = () => {
    if (!searchData || loadedCount >= searchData.length) {
      setHasMore(false);
      return;
    }

    const nextItems = searchData.slice(loadedCount, loadedCount + 10);
    setItems((prev) => [...prev, ...nextItems]);
    setLoadedCount((prev) => prev + nextItems.length);

    if (loadedCount + 10 >= searchData.length) {
      setHasMore(false);
    }
  };

  useEffect(() => {
    if (debouncedSearchValue.trim().length > 1) {
      searchDataMutate(encodeURIComponent(debouncedSearchValue));
    }
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (searchData && isGetSearchDataSuccess) {
      const initialItems = searchData.slice(0, 10);
      setItems(initialItems);
      setLoadedCount(initialItems.length);
      setHasMore(searchData.length > initialItems.length);
    }
  }, [searchData, isGetSearchDataSuccess]);

  useClickOutside([suggestsRef, inputRef], () => setIsShowSuggests(false));

  const [openFull, setOpenFull] = useState(false);

  return (
    <form
      action="#"
      onSubmit={(e) => {
        e.preventDefault();
      }}
      className={clsx(styles.Search, className, { [styles.active]: openFull })}
    >
      <button className={styles.close_popup} onClick={() => setOpenFull(false)}>
        <CrossIcon />
      </button>
      <div className={styles.wrapper}>
        <LoupeIcon className={styles.loupe} />
        {searchValue && (
          <button
            className={styles.close}
            type="button"
            onClick={() => {
              setSearchValue('');
              onReset && onReset();
            }}
          >
            <CrossIcon className={styles.cross} />
          </button>
        )}
        <input
          ref={inputRef}
          type="text"
          value={searchValue}
          onFocus={(e) => {
            e.stopPropagation();
            setIsShowSuggests(true);
            setOpenFull(true);
          }}
          onKeyDown={(e) => {
            if (e.code === 'Enter') {
              onSearch(encodeURIComponent(searchValue));
              setIsShowSuggests(false);
            }
          }}
          onChange={(e) => {
            setOpenFull(true);
            setSearchValue(e.currentTarget.value);
          }}
          placeholder="Поиск по коду или названию"
        />

        {!isGetSearchDataPending &&
          searchData &&
          searchValue &&
          isShowSuggests && (
            <>
              {searchData ? (
                searchData.length > 0 ? (
                  <div className={styles.suggests} ref={suggestsRef}>
                    {items.map((item) => {
                      let row = item.name
                        ? item.name.replace(
                            new RegExp(`(${searchValue})`, 'ig'),
                            '<b>$1</b>',
                          )
                        : '';

                      if (item.category) row += ` — ${item.category}`;
                      if (item.brand) row += ` (${item.brand})`;
                      if (item.code) row += ` | ${item.code}`;

                      return (
                        <Link
                          key={item.id}
                          to={`/product/${item.slug}`}
                          state={{
                            type: item.isExtra ? 'accessory' : item.type,
                          }}
                          onClick={() => {
                            setIsShowSuggests(false);
                            setSearchValue('');
                            searchData.splice(0, searchData.length);
                            setOpenFull(false);
                          }}
                        >
                          <img
                            src={
                              item.isExtra
                                ? commodityTypes['accessory'].icon
                                : commodityTypes[
                                    item.type as keyof typeof commodityTypes
                                  ].icon
                            }
                            alt={item.type}
                          />
                          <span className={styles.suggestItem}>
                            {HTMLReactParser(row)}
                          </span>
                        </Link>
                      );
                    })}

                    {!isFullSearch && hasMore && (
                      <CircularProgress
                        size={20}
                        className={styles.loader}
                        ref={infiniteRef}
                      />
                    )}
                  </div>
                ) : searchValue !== '' ? (
                  <div className={clsx(styles.suggests, styles.empty)}>
                    <h3>По запросу «{searchValue}» совпадений не найдено.</h3>
                    <p>
                      Попробуйте:
                      <br />
                      — Указать точный код (например, MS-8030000165)
                      <br />— Изменить фильтры
                    </p>
                  </div>
                ) : (
                  <div className={styles.suggests}></div>
                )
              ) : (
                <div className={styles.suggests}></div>
              )}
            </>
          )}
      </div>
      <Button
        className={styles.button}
        onClick={() => onSearch(encodeURIComponent(searchValue))}
      >
        Искать
      </Button>
    </form>
  );
};

export default Search;
